import { useEffect } from 'react';
import { DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, SITE_LOCALE, SITE_NAME, absoluteUrl } from '../data/site';

interface PageMeta {
  title?: string;
  description?: string;
  // Omit for pages that must not declare a canonical URL, such as the 404 page.
  path?: string;
  noindex?: boolean;
  type?: 'website' | 'article';
  image?: string;
  imageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  lang?: string;
  jsonLd?: object;
}

const MAX_TITLE_LENGTH = 60;
const HOME_TITLE = `${SITE_NAME} · Websites, apps & cloud in Puerto Rico`;

// Search results cut titles past about 60 characters, so the brand suffix is dropped when it would not fit.
export const pageTitle = (title: string) => {
  const branded = `${title} · ${SITE_NAME}`;
  return branded.length <= MAX_TITLE_LENGTH ? branded : title;
};

const PREVIEW_NOINDEX =
  Boolean(process.env.REACT_APP_VERCEL_ENV) && process.env.REACT_APP_VERCEL_ENV !== 'production';

const upsertMeta = (attr: 'name' | 'property', key: string, content?: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === undefined) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertCanonical = (href?: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === undefined) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const usePageMeta = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  noindex = false,
  type = 'website',
  image,
  imageAlt,
  publishedTime,
  modifiedTime,
  tags = [],
  lang = 'en',
  jsonLd,
}: PageMeta) => {
  const tagKey = tags.join('|');
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';
  const pageLang = lang === 'es' ? 'es' : 'en';
  const ogLocale = pageLang === 'es' ? 'es_PR' : SITE_LOCALE;

  useEffect(() => {
    const fullTitle = title ? pageTitle(title) : HOME_TITLE;
    const url = path === undefined ? undefined : absoluteUrl(path);
    const imageUrl = absoluteUrl(image || DEFAULT_OG_IMAGE);
    const isArticle = type === 'article';

    document.title = fullTitle;
    document.documentElement.lang = pageLang;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex || PREVIEW_NOINDEX ? 'noindex, nofollow' : 'index, follow');
    upsertCanonical(url);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', ogLocale);
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    if (url) {
      [pageLang, 'x-default'].forEach((code) => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = code;
        link.href = url;
        document.head.appendChild(link);
      });
    }
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:image:alt', imageAlt || fullTitle);
    upsertMeta('property', 'article:published_time', isArticle ? publishedTime : undefined);
    upsertMeta('property', 'article:modified_time', isArticle ? modifiedTime || publishedTime : undefined);
    document.head.querySelectorAll('meta[property="article:tag"]').forEach((node) => node.remove());
    if (isArticle && tagKey) {
      tagKey.split('|').forEach((tag) => {
        const element = document.createElement('meta');
        element.setAttribute('property', 'article:tag');
        element.setAttribute('content', tag);
        document.head.appendChild(element);
      });
    }
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', imageUrl);

    document.head.querySelectorAll('script[data-seo="page"]').forEach((node) => node.remove());
    if (jsonLdText) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seo = 'page';
      script.text = jsonLdText;
      document.head.appendChild(script);
    }
  }, [title, description, path, noindex, type, image, imageAlt, publishedTime, modifiedTime, tagKey, jsonLdText, pageLang, ogLocale]);
};

export default usePageMeta;
